import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import {
  fetchRules,
  createRule as createRuleApi,
  updateRule as updateRuleApi,
  deleteRule as deleteRuleApi,
  type CreateRulePayload,
  type UpdateRulePayload,
} from '@/api/rules';
import type { Rule } from '@/types/email';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { getErrorMessage } from '@/lib/error';

const sortRules = (rules: Rule[]): Rule[] => {
  return [...rules].sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
};

interface RuleStoreState {
  rulesByAccount: Record<string, Rule[]>;
  loadingAccountIds: Set<string>;
  errorByAccount: Record<string, string | null>;
  activeAccountId: string | null;

  setActiveAccount: (accountId: string | null) => Promise<void>;
  getRules: () => Rule[];
  loadRules: (accountId: string) => Promise<Rule[]>;
  createRule: (accountId: string, payload: CreateRulePayload) => Promise<Rule>;
  updateRule: (accountId: string, ruleId: string, payload: UpdateRulePayload) => Promise<Rule>;
  deleteRule: (accountId: string, ruleId: string) => Promise<void>;
  toggleRule: (accountId: string, ruleId: string, enabled: boolean) => Promise<Rule>;
  clear: () => void;
}

const ensureAccountId = (accountId: string | null): string => {
  if (!accountId || accountId === UNIFIED_ACCOUNT_ID) {
    throw new Error('Rules can only be modified for a specific account');
  }
  return accountId;
};

export const useRuleStore = create<RuleStoreState>()(
  conditionalDevtools((set, get) => ({
    rulesByAccount: {},
    loadingAccountIds: new Set(),
    errorByAccount: {},
    activeAccountId: null,

    setActiveAccount: async (accountId) => {
      set({ activeAccountId: accountId });
      if (!accountId || accountId === UNIFIED_ACCOUNT_ID) {
        return;
      }
      if (!get().rulesByAccount[accountId]) {
        await get().loadRules(accountId);
      }
    },

    getRules: () => {
      const accountId = get().activeAccountId;
      if (!accountId || accountId === UNIFIED_ACCOUNT_ID) {
        return [];
      }
      return get().rulesByAccount[accountId] ?? [];
    },

    loadRules: async (accountId) => {
      if (!accountId || accountId === UNIFIED_ACCOUNT_ID) {
        set((state) => ({
          rulesByAccount: {
            ...state.rulesByAccount,
            [UNIFIED_ACCOUNT_ID]: [],
          },
        }));
        return [];
      }

      set((state) => {
        const next = new Set(state.loadingAccountIds);
        next.add(accountId);
        return { loadingAccountIds: next, errorByAccount: { ...state.errorByAccount, [accountId]: null } };
      });

      try {
        const rules = await fetchRules(accountId);
        set((state) => ({
          rulesByAccount: {
            ...state.rulesByAccount,
            [accountId]: sortRules(rules),
          },
        }));
        return rules;
      } catch (error) {
        set((state) => ({
          errorByAccount: {
            ...state.errorByAccount,
            [accountId]: getErrorMessage(error) ?? 'Failed to load rules',
          },
        }));
        throw error;
      } finally {
        set((state) => {
          const next = new Set(state.loadingAccountIds);
          next.delete(accountId);
          return { loadingAccountIds: next };
        });
      }
    },

    createRule: async (accountId, payload) => {
      const targetAccountId = ensureAccountId(accountId);
      const created = await createRuleApi(targetAccountId, payload);
      set((state) => ({
        rulesByAccount: {
          ...state.rulesByAccount,
          [targetAccountId]: sortRules([
            ...(state.rulesByAccount[targetAccountId] ?? []),
            created,
          ]),
        },
      }));
      return created;
    },

    updateRule: async (accountId, ruleId, payload) => {
      const targetAccountId = ensureAccountId(accountId);
      const updated = await updateRuleApi(targetAccountId, ruleId, payload);
      set((state) => ({
        rulesByAccount: {
          ...state.rulesByAccount,
          [targetAccountId]: sortRules(
            (state.rulesByAccount[targetAccountId] ?? []).map((rule) =>
              rule.id === updated.id ? updated : rule
            )
          ),
        },
      }));
      return updated;
    },

    deleteRule: async (accountId, ruleId) => {
      const targetAccountId = ensureAccountId(accountId);
      await deleteRuleApi(targetAccountId, ruleId);
      set((state) => ({
        rulesByAccount: {
          ...state.rulesByAccount,
          [targetAccountId]: (state.rulesByAccount[targetAccountId] ?? []).filter(
            (rule) => rule.id !== ruleId
          ),
        },
      }));
    },

    toggleRule: async (accountId, ruleId, enabled) => {
      return get().updateRule(accountId, ruleId, { enabled });
    },

    clear: () =>
      set({
        rulesByAccount: {},
        loadingAccountIds: new Set(),
        errorByAccount: {},
        activeAccountId: null,
      }),
  }))
);
